import {
	EFxIdSdkAdapterInfoSocialCapability,
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkAdapterStickyBannerShowErrorReason,
	FxIdSdkBaseAdapter,
	IExportedFxIdSdkMethod,
	IFxIdAddToFavoritesRequest,
	IFxIdAddToFavoritesResult,
	IFxIdJoinCommunityRequest,
	IFxIdJoinCommunityResult,
	IFxIdSdkAdapterAuthenticateUserRequest,
	IFxIdSdkAdapterAuthenticateUserResponse,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterGetFriendsRequest,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterInviteFriendsRequest,
	IFxIdSdkAdapterOpenModalRequestDto,
	IFxIdSdkAdapterOpenModalResultDto,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest,
	IFxIdSdkAdapterStickyBannerShowResultDto,
	IFxIdSdkGetFriendsResult,
	IFxIdSdkInviteFriendsResult,
	IFxIdSdkRateApplicationRequest,
	IFxIdSdkRateApplicationResult,
	IFxIdSdkWordsFilterRequest,
	IFxIdSdkWordsFilterResult
} from "@app/SDK/FxIdSdkBaseAdapter";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxGamesLibWebPublicDataBase,
	type FxIdWebFeaturesStoreCreatePaymentRequest
} from "@app/Api/gen";
import SharePayload = FBInstant.SharePayload;
import OpenApiClient from "@app/Api/OpenApiClient";
import Purchase = FBInstant.Purchase;
import InvitePayload = FBInstant.InvitePayload;
import { getAuthToken } from "@app/Api/Auth";
import { withRetry } from "@app/SDK/FxIdSdkUtils";
import { IframeType } from "@app/Screens/WebPlayer/Hooks";
import { FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus } from "@app/Api/gen";
import { openInfoModal } from "@app/Components/Modals/InfoModal";
import { Anchor, Text } from "@mantine/core";
import React from "react";
import friendsImageInlineBase64 from "../Images/friends.jpg?inline";
import { FacebookInstantAdSlot } from "@app/SDK/FacebookInstantAdSlot";
import * as Sentry from "@sentry/react";

// ─── Хранилище подсказок public_order_id для FB Instant покупок ─────────────
//
// При recovery через getPurchasesAsync() (перезагрузка страницы) это единственный
// источник номера нашего ордера на клиенте: FB developerPayload не используется,
// Meta выводит поле из эксплуатации (#fxid-1767). Поэтому сохраняем маппинг
//   purchaseToken → public_order_id
// в localStorage сразу после purchaseAsync. Связь есть только в браузере, где
// начата покупка; cross-device покупки находит серверный fallback/mint.

/** Ключ в localStorage, под которым хранится JSON-объект маппинга. */
const FB_ORDER_HINTS_LS_KEY = "fxid.fbinstant.orderHints";

/** Тип маппинга purchaseToken → public_order_id. */
type FbOrderHintsMap = Record<string, string>;

/**
 * Считывает весь маппинг purchaseToken → public_order_id из localStorage.
 * При любой ошибке (недоступен localStorage, невалидный JSON) возвращает
 * пустой объект и логирует предупреждение — никогда не бросает исключение.
 */
function readOrderHints(): FbOrderHintsMap {
	try {
		const raw = localStorage.getItem(FB_ORDER_HINTS_LS_KEY);
		if (!raw) return {};
		return JSON.parse(raw) as FbOrderHintsMap;
	} catch (e) {
		log.warn("[FB-INSTANT] Не удалось прочитать orderHints из localStorage: %o", e);
		return {};
	}
}

/**
 * Записывает маппинг purchaseToken → public_order_id в localStorage.
 * @param token purchaseToken покупки из FB SDK
 * @param publicOrderId public_order_id нашего заказа (TransactionId)
 * При любой ошибке логирует предупреждение и деградирует без исключения.
 */
function writeOrderHint(token: string, publicOrderId: string): void {
	try {
		const hints = readOrderHints();
		hints[token] = publicOrderId;
		localStorage.setItem(FB_ORDER_HINTS_LS_KEY, JSON.stringify(hints));
	} catch (e) {
		log.warn("[FB-INSTANT] Не удалось сохранить orderHint в localStorage: %o", e);
	}
}

/**
 * Удаляет запись для purchaseToken из маппинга после успешного consume.
 * Предотвращает неограниченный рост хранилища.
 * При любой ошибке логирует предупреждение и деградирует без исключения.
 */
function deleteOrderHint(token: string): void {
	try {
		const hints = readOrderHints();
		if (!(token in hints)) return;
		delete hints[token];
		localStorage.setItem(FB_ORDER_HINTS_LS_KEY, JSON.stringify(hints));
	} catch (e) {
		log.warn("[FB-INSTANT] Не удалось удалить orderHint из localStorage: %o", e);
	}
}

/** Операции платёжного API FBInstant, сбои которых отправляются в Sentry. */
type FbInstantPaymentsOperation = "getPurchasesAsync" | "consumePurchaseAsync";

/**
 * Отправляет в Sentry сбой платёжного API FBInstant (#fxid-1798).
 * FB SDK реджектит промисы plain-объектами {code, message}: без обёртки Sentry сваливает их в общую
 * группу "Object captured as promise rejection with keys: code, message" без какого-либо контекста.
 * Поэтому шлём Error с операцией и кодом в тексте (отдельная группа на пару операция+код), а исходную
 * ошибку и данные покупки — в теги и контекст, чтобы искать по игре, токену покупки и игроку.
 * @param operation вызванный метод FBInstant.payments
 * @param error исходная ошибка FB SDK
 * @param game системное имя игры
 * @param purchase покупка, с которой работали (для getPurchasesAsync отсутствует)
 */
function captureFbInstantPaymentsError(
	operation: FbInstantPaymentsOperation,
	error: unknown,
	game: string,
	purchase?: Purchase
): void {
	const fbError = error as Partial<FBInstant.APIError> | null | undefined;
	const code = typeof fbError?.code === "string" ? fbError.code : error instanceof Error ? error.name : "NO_CODE";
	const message = typeof fbError?.message === "string" ? fbError.message : String(error);

	// FB Player ID — для корреляции события с аккаунтом через social identifier в БД. FBInstant.player
	// может быть ещё недоступен (SDK не инициализирован) — тогда событие уходит без него.
	let playerId: string | undefined;
	try {
		playerId = FBInstant.player.getID() ?? undefined;
	} catch {
		// Player API недоступен — отправляем событие без playerId
	}

	Sentry.captureException(new Error(`FBInstant.payments.${operation} failed: ${code}`), {
		tags: {
			"fbinstant.operation": operation,
			"fbinstant.error_code": code,
			game
		},
		contexts: {
			fbinstant_payment: {
				message,
				playerId,
				productId: purchase?.productID,
				purchaseToken: purchase?.purchaseToken,
				paymentId: purchase?.paymentID
			}
		},
		extra: { rawError: error }
	});
}
// ─────────────────────────────────────────────────────────────────────────────

export interface FacebookUserNode {
	id: string;
	first_name?: string;
	last_name?: string;
	middle_name: string;
	name: string;
	picture?: {
		data: {
			height: number;
			is_silhouette: boolean;
			url: string;
			width: number;
		};
	};
}

export interface FacebookFriendsResult {
	data: FacebookUserNode[];
	paging: unknown;
	summary: {
		total_count: number;
	};
}

export class FxIdSdkAdapterForFacebookInstantGames extends FxIdSdkBaseAdapter {
	readonly adapter: IframeType = "fbinstantgames";
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = {
		...FxIdSdkAdapterSocialSettingDefault,
		ui: {
			...FxIdSdkAdapterSocialSettingDefault.ui,
			disabled: false
		}
	};

	private _fbInstantPaymentsReady: boolean = false;
	private _rewardedAdSlot?: FacebookInstantAdSlot;
	private _interstitialAdSlot?: FacebookInstantAdSlot;

	/**
	 * Кеш результата IsZeroPermissionSession. Заполняется при первом вызове
	 * EnsureNezpDetected, последующие вызовы возвращают кешированное значение.
	 */
	private _isNezpSession: boolean | null = null;

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
	}

	async Initialize(): Promise<void> {
		log.info("Loading facebook script");
		await new Promise<void>((resolve) => {
			const s = document.createElement("script");
			s.type = "text/javascript";
			s.src = `https://www.facebook.com/assets.php/en_US/fbinstant.8.0.js`;
			s.async = false;
			s.defer = false;
			s.crossOrigin = "anonymous";
			s.addEventListener(
				"load",
				(e) => {
					log.info("Facebook instant sdk loaded");
					resolve();
				},
				false
			);
			const head = document.getElementsByTagName("head")[0];
			head.appendChild(s);

			log.info("Facebook script loaded");
		});

		log.info("Initializing facebook instant sdk");
		await FBInstant.initializeAsync();

		log.info("Tell fb instant that game started");
		await FBInstant.startGameAsync();

		// Диагностика SDK среды — версия, доступные signed-методы.
		// Помогает отличить v8.0+ NEZP-конфигурацию от legacy и понять
		// какие методы аутентификации физически доступны для этого FB App.
		// Для isZeroPermissionSessionAsync проверяем именно typeof (а не
		// supportedAPIs) — на WEB FB не сюрфейсит метод в supportedAPIs, но
		// мы всё равно полагаемся на ASID-null detection в IsZeroPermissionSession.
		try {
			const sdkVersion = FBInstant.getSDKVersion();
			const platform = FBInstant.getPlatform();
			const supportedApis = FBInstant.getSupportedAPIs();
			const fbInstantUntyped = FBInstant as unknown as { isZeroPermissionSessionAsync?: unknown };
			log.info("[FB-INSTANT] Diagnostics: SDK version=%s, platform=%s", sdkVersion, platform);
			log.info(
				"[FB-INSTANT] Diagnostics: has getSignedASIDAsync=%o, has getSignedPlayerInfoAsync=%o, has isZeroPermissionSessionAsync=%o",
				supportedApis.includes("player.getSignedASIDAsync"),
				supportedApis.includes("player.getSignedPlayerInfoAsync"),
				typeof fbInstantUntyped.isZeroPermissionSessionAsync === "function"
			);
		} catch (e) {
			log.error("[FB-INSTANT] Diagnostics: getSDKVersion/getPlatform threw: %o", e);
		}

		if (this.FBInstantPaymentsApisAvailable()) {
			log.info("Payments apis are available. Subscribing to on ready callback");
			FBInstant.payments.onReady(this.OnFBInstantPaymentsReady.bind(this));
		} else {
			log.info("Payments apis are not available");
		}

		void this.InitializeAds();
	}

	private FBInstantPaymentsApisAvailable() {
		const supportedApis = FBInstant.getSupportedAPIs();
		log.info("List of supported apis: %o", supportedApis);
		// https://developers.facebook.com/docs/games/monetize/in-app-purchases#detecting-whether-payments-is-available
		const paymentsAvailable = supportedApis.includes("payments.purchaseAsync");
		return paymentsAvailable;
	}

	private FBInstantRewardedVideoApiAvailable() {
		const supportedApis = FBInstant.getSupportedAPIs();
		const available = supportedApis.includes("getRewardedVideoAsync");
		return available;
	}

	private FBInstantInterstitialApiAvailable() {
		const supportedApis = FBInstant.getSupportedAPIs();
		const available = supportedApis.includes("getInterstitialAdAsync");
		return available;
	}

	/**
	 * Определяет NEZP (Zero Permissions) сессию. На разных платформах FB сюрфейсит
	 * разные сигналы:
	 *  - mobile (Android/iOS) — canonical API `FBInstant.isZeroPermissionSessionAsync()`
	 *  - web — canonical API отсутствует (даже в SDK 8.0), используем
	 *    `player.getSignedASIDAsync()` и проверяем null ASID per FB v8.0 docs:
	 *    "This function will only return ASIDs for users migrating over from your
	 *    canvas app or for users that had previously accepted Terms of Service".
	 *    null ASID в signed-объекте = NEZP-юзер.
	 *
	 * ВАЖНО: используем именно SIGNED-вариант ASID-probe (тот же что и auth-flow в
	 * FxIdSdk.LoginToFxId для FacebookInstantGames). Раньше fallback брал unsigned
	 * `getASIDAsync` — но на WEB SDK эти два API рассогласованы: для NEZP-юзера
	 * signed-flow возвращает signed-объект с null ASID (correct NEZP signal), а
	 * unsigned может вернуть non-null строку или throw'нуть. В результате auth-flow
	 * корректно определял NEZP (логинил через signed Player ID), а capabilities-flow
	 * — нет, после чего `GetSocialInfo` пытался дёрнуть `FBInstant.player.getName()`
	 * — функцию, которой в NEZP-сессии физически нет в SDK → TypeError
	 * "getName is not a function" рушил весь login.
	 *
	 * Транзиентный fail probe'а (NETWORK_FAILURE и т.п.) — обрабатываем как NEZP,
	 * чтобы не словить runtime crash на getName/getPhoto/getConnectedPlayers для
	 * NEZP-юзера. Эта функция влияет только на capabilities-flow (`GetSocialInfo`,
	 * `GetFriends`, `InviteFriends`); auth-path не зависит от неё — он вызывает
	 * `getSignedASIDAsync` независимо и пробросит исключение наверх если FB реально
	 * недоступен.
	 */
	public async IsZeroPermissionSession(): Promise<boolean> {
		const fbInstantUntyped = FBInstant as unknown as {
			isZeroPermissionSessionAsync?: () => Promise<boolean>;
		};

		if (typeof fbInstantUntyped.isZeroPermissionSessionAsync === "function") {
			try {
				return await fbInstantUntyped.isZeroPermissionSessionAsync();
			} catch (e) {
				log.warn("isZeroPermissionSessionAsync threw, falling back to signed ASID probe: %o", e);
			}
		}

		try {
			const signedAsidData = await (FBInstant as unknown as {
				player: { getSignedASIDAsync(): Promise<{ getASID(): string | null } | null> };
			}).player.getSignedASIDAsync();
			const asid = signedAsidData?.getASID() ?? null;
			return asid == null;
		} catch (e) {
			log.warn("getSignedASIDAsync probe threw, assuming NEZP for capability safety: %o", e);
			return true;
		}
	}

	/**
	 * Кешированная NEZP-detection. Первый вызов — round-trip к FB SDK через
	 * IsZeroPermissionSession; последующие — мгновенные. Используется всеми
	 * внутренними consumer'ами адаптера для согласованного решения о NEZP-режиме
	 * без повторных вызовов SDK.
	 */
	private async EnsureNezpDetected(): Promise<boolean> {
		if (this._isNezpSession == null) {
			this._isNezpSession = await this.IsZeroPermissionSession();
		}
		return this._isNezpSession;
	}

	OnFBInstantPaymentsReady() {
		log.info("FB instant payments ready");
		this._fbInstantPaymentsReady = true;
		void this.CheckNotConsumedPurchases();
	}

	private async CheckNotConsumedPurchases() {
		log.info("Checking purchases");
		let unconsumedPurchases: Purchase[];
		try {
			unconsumedPurchases = await FBInstant.payments.getPurchasesAsync();
		} catch (e) {
			// Без catch отказ FB уходил в unhandled rejection без контекста, и было не видно, почему
			// recovery не доводит consume зависших покупок (они блокируют повторную покупку товара). #fxid-1798
			log.error("Failed to get unconsumed purchases: %o", e);
			captureFbInstantPaymentsError("getPurchasesAsync", e, this.game);
			return;
		}

		log.info("Found unconsumed purchases %o", unconsumedPurchases);

		for (const purchase of unconsumedPurchases) {
			try {
				await this.VerifyAndConsumePurchase(purchase);
			} catch (e) {
				log.error("Failed to verify and consume purchase: %o", purchase);
				log.error(e);
			}
		}
	}

	async GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		// EnsureNezpDetected probe'ит canonical API + fallback на ASID-null check.
		// isNezp используется для capabilities/displayName/photo — эти поля
		// принципиально недоступны в NEZP-сессии и должны быть undefined.
		const isNezp = await this.EnsureNezpDetected();
		let userId: string | null = null;
		if (!isNezp) {
			// TOS-юзер: ASID должен быть доступен (probe в EnsureNezpDetected
			// только что вернул non-null). Транзиентный throw (NETWORK_FAILURE)
			// пробрасываем — не падаем на PlayerId, чтобы не создать дубликат аккаунта.
			userId = await FBInstant.player.getASIDAsync();
		}

		if (userId == null) {
			// NEZP-юзер или ASID отдал null между probe'ом и этим вызовом —
			// используем стабильный для платформы Player ID. Никогда не бросает.
			userId = FBInstant.player.getID();
		}

		if (userId == null) {
			throw new Error("Failed to retrieve userId from FBInstant (both ASID and Player ID are null)");
		}

		const paymentsAvailable = this.FBInstantPaymentsApisAvailable();

		// FB Instant Games переходит на Network Enabled Zero Permissions (NEZP),
		// обязательный с 30.09.2026. В NEZP-модели игра НЕ имеет прямого доступа к
		// имени/фото юзера и друзей — Meta рендерит их в отдельных overlay-view iframe
		// "without having access to the data within". Поэтому НЕ вызываем getName/
		// getPhoto (FB вырезает эти методы из FBInstant.player даже у юзеров с ASID —
		// см. TypeError "getName is not a function" при инициализации) и не анонсируем
		// friends-capabilities (прямого доступа к друзьям нет; рендер друзей — только
		// через overlay views, отдельная будущая фича). displayName/photo остаются
		// undefined, capabilities — только реклама.
		const capabilities = [EFxIdSdkAdapterInfoSocialCapability.AdsAvailable];

		const result: IFxIdSdkAdapterInfoSocial = {
			social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames,
			paymentsAvailable: paymentsAvailable,
			userId: userId,
			capabilities,
			socialLocale:
				FBInstant.getLocale() == null || FBInstant.getLocale() == ""
					? undefined
					: (FBInstant.getLocale() ?? undefined)
		};

		return result;
	}

	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		if (!this._fbInstantPaymentsReady) {
			log.warn("Payments api still not ready");
			throw new Error("Payments api still not ready");
		}

		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			ProductNameHint: request.productNameHint,
			ProductDescriptionHint: request.productDescriptionHint,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.FacebookInstantGames,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed
		};

		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = {
				MerchantDeveloperPayload: request.developerPayload
			};
		}

		const createPaymentResult = await OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest);

		log.info("Received result from server: %o", createPaymentResult);

		try {
			const productId = createPaymentResult.OrderProduct.UsedTieredProducts ? createPaymentResult.OrderProduct.TieredProductsSku! : createPaymentResult.FacebookInstantGames!.StoreSpecificOrderProductSku;

			const fbInstantPurchase = await FBInstant.payments.purchaseAsync({
				productID: productId
			});

			log.info("Received purchase from fb instant: %o", fbInstantPurchase);

			// Сохраняем маппинг purchaseToken → public_order_id до вызова VerifyAndConsumePurchase:
			// при recovery через getPurchasesAsync это единственный источник номера ордера на клиенте.
			writeOrderHint(fbInstantPurchase.purchaseToken, createPaymentResult.TransactionId);

			await this.VerifyAndConsumePurchase(fbInstantPurchase, createPaymentResult.TransactionId);

			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint(
				{
					PublicOrderId: createPaymentResult.TransactionId,
					Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed
				}
			).catch((e) => log.error("[FBINSTANT] Failed to set optimistic status:", e));

			return {
				transactionId: createPaymentResult.TransactionId,
				stats: {
					currency: createPaymentResult.OrderProduct.Currency ?? "",
					currencyAmount: createPaymentResult.OrderProduct.Price ?? 0,
					usdAmount: createPaymentResult.OrderProduct.UsdPrice ?? 0
				}
			};
		} catch (error) {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint(
				{
					PublicOrderId: createPaymentResult.TransactionId,
					Status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled
				}
			).catch((e) => log.error("[FBINSTANT] Failed to set optimistic status:", e));
			throw error;
		}
	}

	SocialSettings(): Promise<IFxIdSdkAdapterSocialSettings> {
		return Promise.resolve({
			ui: {
				disabled: true
			}
		});
	}

	RegisterShareHandlers(): Promise<void> {
		return Promise.resolve();
	}

	StoreCurrency(): Promise<string | undefined> {
		return Promise.resolve(undefined);
	}

	StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		return Promise.resolve();
	}

	StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve();
	}

	async StoreAdditionalData(): Promise<unknown | undefined> {
		if (!this.FBInstantPaymentsApisAvailable()) {
			return undefined;
		}

		const catalog = await withRetry(
			() => FBInstant.payments.getCatalogAsync(),
			{
				maxAttempts: 3,
				baseDelayMs: 1000,
				maxDelayMs: 5000,
				onRetry: (error, attempt, delayMs) => {
					log.warn("Catalog fetch failed (attempt %d), retrying in %dms: %o", attempt, delayMs, error);
				}
			}
		);
		return { catalog };
	}

	/**
	 * Возвращает готовность независимого rewarded-slot; при отсутствии placement-а реклама недоступна.
	 */
	async AdsIsVideoReady(): Promise<boolean> {
		return this._rewardedAdSlot?.isReady() ?? false;
	}

	/**
	 * Делегирует показ rewarded-slot, который отправляет ровно один terminal callback; без slot отправляет failed.
	 */
	async AdsShowVideo(): Promise<void> {
		if (this._rewardedAdSlot == null) {
			window.FxIdSdk!.DispatchAdsFailed();
			return;
		}

		await this._rewardedAdSlot.show();
	}

	/**
	 * Возвращает готовность независимого interstitial-slot; при отсутствии placement-а реклама недоступна.
	 */
	async AdsIsInterstitialReady(): Promise<boolean> {
		return this._interstitialAdSlot?.isReady() ?? false;
	}

	/**
	 * Делегирует показ interstitial-slot, который отправляет ровно один terminal callback; без slot отправляет failed.
	 */
	async AdsShowInterstitial(): Promise<void> {
		if (this._interstitialAdSlot == null) {
			window.FxIdSdk!.DispatchAdsFailed();
			return;
		}

		await this._interstitialAdSlot.show();
	}

	AdsStickyBannerShow(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	AdsStickyBannerHide(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	async GetFriends(_request: IFxIdSdkAdapterGetFriendsRequest): Promise<IFxIdSdkGetFriendsResult> {
		// В NEZP-модели FB Instant Games (обязательна с 30.09.2026) прямого доступа к
		// данным друзей нет — getConnectedPlayersAsync/friend.getName/getPhoto не отдают
		// данные игре. Рендер друзей возможен только через Meta-managed overlay views
		// (отдельная будущая фича). Поэтому возвращаем пустой список.
		return { friends: [] } as any;
	}

	async InviteFriends(_request: IFxIdSdkAdapterInviteFriendsRequest): Promise<IFxIdSdkInviteFriendsResult> {
		if (await this.EnsureNezpDetected()) {
			log.info("[FB-INSTANT] InviteFriends short-circuit: NEZP session, invites not available");
			return { friends: [] };
		}

		let image: string;

		// FB Instant Games требует изображение в формате base64 для отправки приглашения
		// Если изображение для приглашения не предоставлено в запросе, используем изображение по умолчанию
		if (_request.base64image) {
			image = _request.base64image;
		} else {
			log.debug("base64image for invite is not provided, using default one");
			image = friendsImageInlineBase64;
		}

		const payload: InvitePayload = {
			image,
			text: _request.inviteText,
			data: _request.trackString
		};

		await FBInstant.inviteAsync(payload);

		return { friends: [] };
	}

	AuthenticateUser(
		_request: IFxIdSdkAdapterAuthenticateUserRequest
	): Promise<IFxIdSdkAdapterAuthenticateUserResponse> {
		return Promise.resolve({});
	}

	JoinCommunity(_request: IFxIdJoinCommunityRequest): Promise<IFxIdJoinCommunityResult> {
		return Promise.resolve({ success: false });
	}

	async AddToFavorites(_request: IFxIdAddToFavoritesRequest): Promise<IFxIdAddToFavoritesResult> {
		const canCreateShortcut = await FBInstant.canCreateShortcutAsync();
		if (canCreateShortcut) {
			// Тут try/catch нормальная семантика для этого вызова
			try {
				await FBInstant.createShortcutAsync();
				return { success: true };
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch (_e) {
				return { success: false };
			}
		} else {
			return { success: false };
		}
	}

	WordsFilter(request: IFxIdSdkWordsFilterRequest): Promise<IFxIdSdkWordsFilterResult> {
		return Promise.resolve({ success: true });
	}

	RateApplication(request: IFxIdSdkRateApplicationRequest): Promise<IFxIdSdkRateApplicationResult> {
		return Promise.resolve({ success: true });
	}

	async LoadingReady(): Promise<void> {
		return Promise.resolve();
	}

	async GameStarted(): Promise<void> {
		return Promise.resolve();
	}

	async GameStopped(): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Отправляет подписанный чек FB на сервер и делает consume, только если сервер зачислил покупку.
	 * publicOrderId — номер нашего ордера: передаётся явно из BuyProduct, при recovery берётся из
	 * localStorage-связи purchaseToken→public_order_id. FB developerPayload не используется (#fxid-1767).
	 */
	private async VerifyAndConsumePurchase(purchase: Purchase, publicOrderId?: string) {
		log.info("Verifying payment on server %o", purchase);

		const resolvedPublicOrderId = publicOrderId ?? readOrderHints()[purchase.purchaseToken];

		let shouldConsume = false;
		try {
			const response =
				await OpenApiClient.FacebookInstantGames.fxIdWebFeaturesStoreFacebookInstantGamesPurchaseEndpoint({
					Game: this.game,
					FbInstantPurchaseSignedData: purchase.signedRequest,
					PublicOrderId: resolvedPublicOrderId
				});

			log.info("Received response from server: %o", response);

			if (!response.Success) {
				log.error(`Purchase not validated ${JSON.stringify(purchase)}`);
			}

			// Consume только если сервер подтвердил (backward-compatible с сервером без ShouldConsume)
			shouldConsume = response.ShouldConsume !== false;

			// Удаляем hint из localStorage только при успехе, чтобы повторная
			// попытка при следующей загрузке снова нашла подсказку в случае неудачи.
			if (shouldConsume) {
				deleteOrderHint(purchase.purchaseToken);
			}
		} catch (e) {
			// При сетевой ошибке НЕ consume — покупка остаётся unconsumed для retry при следующей загрузке
			log.error(e);
			shouldConsume = false;
		}

		if (shouldConsume) {
			try {
				await FBInstant.payments.consumePurchaseAsync(purchase.purchaseToken);
			} catch (e) {
				// Непогашенная покупка остаётся у FB и блокирует повторную покупку этого товара
				// ("Existing request pending"), пока recovery не доведёт consume. #fxid-1798
				captureFbInstantPaymentsError("consumePurchaseAsync", e, this.game, purchase);
				throw e;
			}
		} else {
			log.warn("Not consuming purchase %s — will retry on next load", purchase.purchaseToken);
		}
	}

	/**
	 * Создаёт и запускает независимые рекламные slots после завершения FBInstant.startGameAsync только для настроенных placement-ов.
	 */
	private InitializeAds() {
		const rewardedConfig = this.config.PublicWebClientConfig.Advertisement?.FacebookInstantGames?.Rewarded;
		const interstitialConfig = this.config.PublicWebClientConfig.Advertisement?.FacebookInstantGames?.Interstitial;
		if (rewardedConfig != null) {
			this._rewardedAdSlot = new FacebookInstantAdSlot({
				format: "rewarded",
				placementId: rewardedConfig.DefaultPlacementId,
				isApiAvailable: () => this.FBInstantRewardedVideoApiAvailable(),
				createAdInstance: (placementId) => FBInstant.getRewardedVideoAsync(placementId),
				onAvailable: () => window.FxIdSdk!.DispatchAdsVideoAvailable(),
				onFinished: () => window.FxIdSdk!.DispatchAdsFinished(),
				onFailed: () => window.FxIdSdk!.DispatchAdsFailed(),
				setTimeout: globalThis.setTimeout,
				clearTimeout: globalThis.clearTimeout
			});
			this._rewardedAdSlot.start();
		}

		if (interstitialConfig != null) {
			this._interstitialAdSlot = new FacebookInstantAdSlot({
				format: "interstitial",
				placementId: interstitialConfig.DefaultPlacementId,
				isApiAvailable: () => this.FBInstantInterstitialApiAvailable(),
				createAdInstance: (placementId) => FBInstant.getInterstitialAdAsync(placementId),
				onAvailable: () => window.FxIdSdk!.DispatchAdsInterstitialAvailable(),
				onFinished: () => window.FxIdSdk!.DispatchAdsFinished(),
				onFailed: () => window.FxIdSdk!.DispatchAdsFailed(),
				setTimeout: globalThis.setTimeout,
				clearTimeout: globalThis.clearTimeout
			});
			this._interstitialAdSlot.start();
		}
	}

	/**
	 * Открывает диалоговое окно со ссылкой вместо iframe, так как FB Instant Games не поддерживает iframes.
	 * @param requestJson JSON-строка с параметрами для открытия модального окна.
	 * @returns Результат операции.
	 */
	async OpenModal(requestJson: string): Promise<IFxIdSdkAdapterOpenModalResultDto> {
		const request: IFxIdSdkAdapterOpenModalRequestDto = JSON.parse(requestJson);
		const message = (
			<Text>
				Open link:{" "}
				<Anchor href={request.Url} target="_blank" rel="noopener noreferrer">
					{request.Url}
				</Anchor>
			</Text>
		);
		openInfoModal(message);
		return { Success: true };
	}
}
